/**
 * A REFERENCE field, as a picker instead of a uuid.
 *
 * The target is derived from the metamodel's FK relationship. A monomorphic
 * declaration yields one picker. A discriminated FK yields every declared
 * target and treats `(discriminator, id)` as one value: neither half is guessed
 * or written alone. A ref with no declared/caller-visible target fails closed:
 * the stored identifier is neither rendered nor searchable.
 */
import { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ReadOnlyLinkField, RelationField, type LookupItem, type LookupProps, type LookupType } from '@solum/ui'
import type { SchemaDescription } from '@solum/sdk'
import { useSolum } from '../data/client'
import { displayLabel, useLookupLabels, useRecordIdentities, useSchema, useSemantics } from '../data/queries'
import { deriveRelationSlots } from '../record/relations'
import { useSearchTools } from '../record/useRelations'
import { advancedLookupFields, makeEntitySearch } from './entityLookup'

export interface RefTarget {
  entity: string
  label: string
  discriminatorValue: string | null
  cardinality?: SchemaDescription['relationships'][number]['cardinality']
}

export type RefTargetResolution =
  | { kind: 'none'; reason: 'schema-unavailable' | 'undeclared' | 'invalid-polymorphic-declaration' }
  | { kind: 'single'; target: RefTarget; discriminatorColumn: string | null }
  | { kind: 'polymorphic'; targets: RefTarget[]; discriminatorColumn: string }

/** Every entity a ref column may point at. Sorting affects presentation only. */
export function refTarget(
  schema: SchemaDescription | undefined, entity: string, column: string,
): RefTargetResolution {
  if (!schema) return { kind: 'none', reason: 'schema-unavailable' }
  const slots = deriveRelationSlots(schema, entity).filter(
    (slot) => slot.kind === 'field' && slot.storage === 'fk' && slot.fkColumn === column)
  if (slots.length === 0) return { kind: 'none', reason: 'undeclared' }

  const targets = slots.map((slot): RefTarget => ({
    entity: slot.otherEntity,
    label: slot.otherLabel,
    discriminatorValue: slot.discriminatorValue,
    cardinality: slot.cardinality,
  }))
  if (targets.length === 1) {
    return { kind: 'single', target: targets[0]!, discriminatorColumn: slots[0]!.discriminatorColumn }
  }
  const columns = new Set(slots.map((slot) => slot.discriminatorColumn).filter((value): value is string => !!value))
  if (columns.size !== 1 || slots.some((slot) => !slot.discriminatorValue)) {
    return { kind: 'none', reason: 'invalid-polymorphic-declaration' }
  }
  return { kind: 'polymorphic', targets, discriminatorColumn: [...columns][0]! }
}

/** One parent-form patch for the composite pointer. */
export function refValuePatch(
  column: string,
  resolution: Exclude<RefTargetResolution, { kind: 'none' }>,
  id: string | null,
  selectedType: string | null,
  currentType: string | null,
): Record<string, unknown> | null {
  const discriminatorColumn = resolution.discriminatorColumn
  if (!discriminatorColumn) return { [column]: id }
  const target = resolution.kind === 'single'
    ? resolution.target
    : resolution.targets.find((candidate) => candidate.discriminatorValue === selectedType)
  const token = target?.discriminatorValue ?? null
  if (!token) return null
  if (id == null && currentType === token) return { [column]: null }
  return { [discriminatorColumn]: token, [column]: id }
}

interface CompositeRefPickerProps {
  id?: string
  resolution: RefTargetResolution
  column: string
  value: string | null
  discriminatorValue: string | null
  onChange: (patch: Record<string, unknown>) => void
  disabled?: boolean
  testid?: string
  ariaLabel?: string
  minChars?: number
}

interface LiteralRefPickerProps {
  id?: string
  target: { entity: string; label: string }
  value: string | null
  onChange: (id: string | null) => void
  disabled?: boolean
  testid?: string
  ariaLabel?: string
  minChars?: number
}

export interface ReadOnlyReferenceProps {
  entity: string | null
  value: string | null
  label: string | null
  loading?: boolean
  testid?: string
}

/**
 * The read-only half of a governed reference. Routing belongs to the Console,
 * not the data-free field renderer; labels belong to the target entity's
 * registered display contract, not to the stored UUID.
 *
 * Four cases (#2757 ROOT ruling c.110809 R2): an UNSET reference is an empty
 * slot (`—`), never "Record unavailable" — absent is not refused; a readable
 * target carries its governed title, or `Unnamed {type}` when its title
 * expression yields nothing; a missing, hidden or refused target, or a failed
 * read, is "Record unavailable" with no former label and no id.
 */
export function ReadOnlyReference({ entity, value, label, loading = false, testid }: ReadOnlyReferenceProps) {
  if (!value) {
    return <span data-testid={testid} data-reference-unset="">—</span>
  }
  if (entity && loading) {
    return <span role="status" data-testid={testid}>Loading record…</span>
  }
  if (!entity || !value || !label) {
    return <span role="status" data-testid={testid}>Record unavailable</span>
  }
  return (
    <ReadOnlyLinkField href={`/e/${encodeURIComponent(entity)}/${encodeURIComponent(value)}`}
      data-record-id={value} data-testid={testid}>{label}</ReadOnlyLinkField>
  )
}

export function RefPicker(props: CompositeRefPickerProps): React.ReactElement
export function RefPicker(props: LiteralRefPickerProps): React.ReactElement
export function RefPicker(props: CompositeRefPickerProps | LiteralRefPickerProps) {
  const literal = 'target' in props
  const resolution: RefTargetResolution = literal
    ? { kind: 'single', target: { ...props.target, discriminatorValue: null }, discriminatorColumn: null }
    : props.resolution
  const column = literal ? '' : props.column
  const discriminatorValue = literal ? null : props.discriminatorValue
  const { id, value, disabled, testid, ariaLabel, minChars } = props
  const { client } = useSolum()
  const queryClient = useQueryClient()
  const schema = useSchema()
  const searchTools = useSearchTools()
  const activeTarget = resolution.kind === 'none'
    ? null
    : resolution.kind === 'single'
    ? resolution.target
    : resolution.targets.find((target) => target.discriminatorValue === discriminatorValue) ?? null
  const activeEntity = activeTarget?.entity ?? ''
  /* The raw target-table read serves only the EDITABLE picker's current item.
     The read-only path below names the target through the governed identity
     alone; asking for the row as well read a record nobody is shown, and for a
     subject-audited target receipted that read (#2757 R2). */
  const current = useMemo(() => (!disabled && value && activeTarget ? [value] : []), [disabled, value, activeTarget])
  const labels = useLookupLabels(activeEntity, current)
  /* The read-only label is the target's GOVERNED identity — core.titles_of
     plus the registered entity identity — never `displayLabel(row)`, whose
     name-column guess turned a readable unnamed payment or invoice into
     "Record unavailable" (#2757 R2). */
  const identityRefs = useMemo(
    () => (disabled && value && activeEntity ? [{ type: activeEntity, id: value }] : []),
    [disabled, value, activeEntity])
  const identities = useRecordIdentities(identityRefs)
  const semantics = useSemantics(activeEntity, activeEntity !== '')
  const advancedFields = advancedLookupFields(
    (semantics.data?.fields ?? []).filter((field) => field.entity_type === activeEntity))
  const types: LookupType[] | undefined = resolution.kind === 'polymorphic'
    ? resolution.targets.map((target) => ({ key: target.discriminatorValue!, label: target.label }))
    : undefined

  if (disabled) {
    const naming = value && activeEntity ? identities.identity(activeEntity, value) : null
    return <ReadOnlyReference entity={activeTarget?.entity ?? null} value={value}
      label={naming && naming.present && naming.state !== 'loading' ? naming.title : null}
      loading={naming?.state === 'loading'} testid={testid} />
  }

  const search: LookupProps['search'] = async (query, facet, selectedType, filters) => {
    if (resolution.kind === 'none') return []
    const target = resolution.kind === 'single'
      ? resolution.target
      : resolution.targets.find((candidate) => candidate.discriminatorValue === selectedType)
    if (!target) return []
    return makeEntitySearch(client.raw(), client, schema.data, target.entity, queryClient, { searchTools })(
      query, facet, selectedType, filters)
  }

  const apply = (id: string | null, selectedType: string | null) => {
    if (literal) {
      props.onChange(id)
      return
    }
    if (resolution.kind === 'none') return
    const patch = refValuePatch(column, resolution, id, selectedType, discriminatorValue)
    if (patch) props.onChange(patch)
  }

  return (
    <div>
      {resolution.kind === 'polymorphic' && !activeTarget && (
        <small role="status" data-testid={testid ? `${testid}-type-required` : undefined}>
          Choose what this reference points to. The stored value has not been changed.
        </small>
      )}
      <RelationField
        id={id}
        value={value}
        onChange={apply}
        onTypeChange={(selectedType) => {
          if (!literal && resolution.kind !== 'none') {
            props.onChange({ [resolution.discriminatorColumn!]: selectedType, [column]: null })
          }
        }}
        disabled={disabled}
        types={types}
        defaultType={activeTarget?.discriminatorValue ?? undefined}
        placeholder={activeTarget ? `Find ${activeTarget.label.toLowerCase()}…` : 'Choose a reference type…'}
        testid={testid}
        ariaLabel={ariaLabel}
        minChars={minChars}
        search={search}
        advanced={advancedFields.length ? { fields: advancedFields } : undefined}
        unavailableMessage={resolution.kind === 'none' ? 'This relationship is unavailable.' : undefined}
        display={(id): LookupItem => {
          const row = labels.byId.get(id)
          return { id, primary: row ? displayLabel(row) : 'Record unavailable' }
        }}
      />
    </div>
  )
}
